import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { SectionLabel } from '../components/SectionLabel';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { SplitSection, ChecklistSection, ProofStrip, FaqSection, PriceSection, RelatedLinks, NapBlock, METRO, BASE_URL, ORG_ID, metroAreaServed } from '../components/LocalPage';

/* 2026-09-17: money page 5 of 5, the location page for the map pack.
   Target query: "answering service phoenix". Carries the NAP block, the
   service-area list, and a LocalBusiness-style schema that points at the
   sitewide organization node. No reviews, no reputation. */

const faqs = [{
  question: 'Do you serve the whole Phoenix metro?',
  answer: 'Yes. Phoenix, Scottsdale, Glendale, Peoria, Chandler, Mesa, Gilbert, Tempe, Queen Creek, Buckeye, Surprise and Goodyear. The receptionist is set up with your own service area, so it only books jobs where you actually go.'
}, {
  question: 'Where is Viridia Analytics located?',
  answer: 'We are based in north Phoenix, in the 85050 zip code, and we meet clients across the Valley. Setup calls happen by video or in person, whichever you prefer.'
}, {
  question: 'Do you answer calls for businesses outside Arizona?',
  answer: 'The system works anywhere in the US. Phoenix is home, and the pages on this site are written for Valley businesses first. If you are outside Arizona, book the call and we will tell you honestly whether it fits.'
}, {
  question: 'What kinds of Phoenix businesses do you work with?',
  answer: 'Home service businesses: landscapers, painters, roofers, plumbers, HVAC, electricians, countertop and cabinet shops, garage doors, windows and remodelers. If the job starts with a phone call and an estimate, it fits.'
}, {
  question: 'How fast can a Phoenix business be live?',
  answer: 'Usually within a week of the setup call. We build the receptionist with you, register your business number for texting, forward your existing line and test it with real calls before it goes live.'
}, {
  question: 'What does an answering service in Phoenix cost?',
  answer: 'The AI receptionist comes with The Viridia System at $897 a month, month to month, including the CRM, missed-call text-back, a business number and the follow-up automations. No per-minute billing.'
}];
const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'ProfessionalService',
    '@id': ORG_ID,
    name: 'Viridia Analytics LLC',
    alternateName: 'Viridia Analytics',
    url: BASE_URL,
    telephone: '+1-623-300-0708',
    email: 'info@viridiaanalytics.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      postalCode: '85050',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '33.7184',
      longitude: '-112.0785'
    },
    areaServed: metroAreaServed,
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00'
    },
    priceRange: '$$'
  }, {
    '@type': 'Service',
    '@id': `${BASE_URL}/answering-service-phoenix#service`,
    name: 'Answering Service in Phoenix',
    serviceType: 'AI answering service',
    description: 'AI receptionist for Phoenix home service businesses that answers every call around the clock, books estimates and texts back missed calls. Part of The Viridia System, $897 a month.',
    url: `${BASE_URL}/answering-service-phoenix`,
    provider: {
      '@id': ORG_ID
    },
    areaServed: metroAreaServed,
    offers: {
      '@type': 'Offer',
      name: 'The Viridia System',
      url: `${BASE_URL}/pricing#viridia-system`,
      priceCurrency: 'USD',
      price: '897'
    }
  }]
};
export function AnsweringServicePhoenix() {
  return <>
      <SEOHead title="Answering Service in Phoenix for Home Service Businesses | Viridia Analytics" description="A Phoenix answering service built for contractors: an AI receptionist that answers around the clock, books the estimate and texts back missed calls. Based in north Phoenix, serving the whole Valley. $897 a month." canonical="/answering-service-phoenix" jsonLd={jsonLd} faq={faqs} data-id="element-1141" data-mp="t0xt6tv2dnhw" />

      <PageHeader label="Phoenix, Arizona" title="Answering service in Phoenix for home service businesses." intro="An answering service in Phoenix that picks up when you are on a job in Peoria, books the estimate, and texts back the calls you miss. Built here, by a former Yelp rep, for Valley contractors." data-id="element-1142" data-mp="16znp4665dh4o">
        <div className="flex flex-wrap items-center gap-4" data-id="element-1143" data-mp="270iw2avg6fw7">
          <CTAButton to="/contact" data-id="element-1144" data-mp="2byu2scqg4aa7">Book a 15-min call</CTAButton>
          <CTAButton href="tel:6233000708" variant="ghost" data-id="element-1145" data-mp="2gwvluxnvg9lo">
            Call (623) 300-0708
          </CTAButton>
        </div>
      </PageHeader>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-1146" data-mp="2kz8r7kypi5hx">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-12 px-6 py-20 lg:grid-cols-[1.2fr_0.8fr] lg:py-24" data-id="element-1147" data-mp="2pwiotnw91ngu">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4
        }} data-id="element-1148" data-mp="2uusgvpmog7y8">
            <SectionLabel data-id="element-1149" data-mp="38o1r1sig8xht">Service area</SectionLabel>
            <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-1150" data-mp="38pcqjg4v3j34">
              Every city in the Valley, and only the ones you work in.
            </h2>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-neutral-400" data-id="element-1151" data-mp="1dloka1we0idd">
              The receptionist is set up with your service area, so a caller in Queen Creek gets
              booked if you go there and gets an honest no if you do not. No more driving an hour
              for an estimate you would have turned down.
            </p>
            <ul className="mt-8 grid max-w-lg grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-1152" data-mp="1x8ujlyh1sbub">
              {METRO.map(city => <li key={city} className="rounded-lg border border-[#1c1c1c] bg-[#0d0d0d] px-3.5 py-2.5 text-sm text-neutral-300" data-id="element-1153" data-mp="1saayg0aa3430">
                  {city}
                </li>)}
            </ul>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: 0.08
        }} data-id="element-1154" data-mp="bv9p0pa2f005">
            <NapBlock data-id="element-1155" data-mp="2iktl8dvoebox" />
          </motion.div>
        </div>
      </section>

      <SplitSection alt flip label="Why Phoenix is different" title="The calls come in at 110 degrees, after a monsoon, on a Sunday." body={<>
            <p data-id="element-1157" data-mp="2dsju4uitr5l2">
              An AC that quits in July, a roof after a haboob, a pool pump in May. Phoenix home
              service calls are urgent and they do not wait for office hours. The caller who gets
              voicemail calls the next name.
            </p>
            <p data-id="element-1158" data-mp="24q0xis5mtp79">
              The receptionist answers on the first ring, books the first open slot, and rings you
              straight through on the words you decide count as an emergency.
            </p>
          </>} visual={<DeviceMockup mode="call" heading="Incoming call, Sunday 4:10 PM" lines={[{
      from: 'caller',
      text: 'Our AC just died and we have a newborn. Is anyone available today?'
    }, {
      from: 'ai',
      text: 'I am putting you through to the on-call tech right now. Can I get your address in case we drop?'
    }]} resultChip="Transferred to owner, address logged" data-id="element-1159" data-mp="3l7onutemll50" />} note="AI receptionist" data-id="element-1156" data-mp="2njeh613wa97k" />

      <ChecklistSection label="What you get" title="What a Phoenix business gets with the receptionist." columns={3} items={[{
      title: 'Answered around the clock',
      body: 'Nights, weekends and holidays, at no extra charge. The after-hours call is the one that becomes a job.'
    }, {
      title: 'Set up for your trade and area',
      body: 'Your services, your cities, your hours, your emergency rules. Built with you on a setup call.'
    }, {
      title: 'Estimates booked on your calendar',
      body: 'The caller gets a time, you get a text with the address and the problem.'
    }, {
      title: 'Missed calls texted back',
      body: 'Anything that gets through gets a text in seconds from your business number.'
    }, {
      title: 'One inbox',
      body: 'Calls, texts, Yelp, Google and web forms in one place, on your phone.'
    }, {
      title: 'A local team',
      body: 'We are in north Phoenix. When something needs changing, you text Kevin.'
    }]} data-id="element-1160" data-mp="1y8xp22xn8akz" />

      <ProofStrip items={[{
      value: '284%',
      label: 'more leads for a Phoenix landscaping company after the system went in',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping, Phoenix'
    }, {
      value: '28',
      label: 'estimates booked by the receptionist in that account',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping, Phoenix'
    }, {
      value: '29',
      label: 'estimates booked by the receptionist for a Valley painting company',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }]} data-id="element-1161" data-mp="3vw8cp77jq2z0" />

      <SplitSection alt label="Yelp and Google" title="Built by someone who used to sell Yelp ads to Phoenix contractors." body={<>
            <p data-id="element-1163" data-mp="1ekp09qngoi3u">
              Kevin worked at Yelp before starting Viridia. He watched Valley businesses pay for
              leads and then miss the call. The receptionist exists because of that.
            </p>
            <p data-id="element-1164" data-mp="1ndsazr6q2gus">
              If you run Yelp or Google ads, the receptionist answers those leads too, and the
              monthly report shows what the spend actually booked.
            </p>
          </>} visual={<DeviceMockup mode="chat" heading="Yelp quote request" lines={[{
      from: 'customer',
      text: 'Looking for a quote on a backyard turf install in Gilbert, about 600 sq ft.'
    }, {
      from: 'system',
      text: 'We can do that. What is the address, and does Thursday morning work for a look?'
    }]} resultChip="Replied in 40 seconds" data-id="element-1165" data-mp="vmmerlu4h3as" />} note="Yelp Ads Management" data-id="element-1162" data-mp="sg1l3z2enw6l" />

      <PriceSection title="One flat price for Phoenix businesses." body={<p data-id="element-1167" data-mp="2l6i42y3cnde4">
            The receptionist, the CRM, missed-call text-back, a business number and the follow-up
            automations. Set up with you, month to month, no contract.
          </p>} data-id="element-1166" data-mp="2q4tcd4dzxfu8" />

      <FaqSection alt title="Questions from Phoenix business owners." faqs={faqs} data-id="element-1168" data-mp="2cdolaqop1vvi" />

      <RelatedLinks title="Related pages" links={[{
      to: '/answering-service-for-contractors',
      label: 'Answering service for contractors',
      body: 'The trades version: emergencies, service areas, and estimates booked from the truck.'
    }, {
      to: '/yelp-ads',
      label: 'Yelp Ads Management',
      body: 'Yelp advertising run by a former Yelp rep, reported against booked jobs.'
    }, {
      to: '/google-business-profile-setup',
      label: 'Google Business Profile setup',
      body: 'Getting your profile verified and set up so Phoenix searches find you.'
    }]} data-id="element-1169" data-mp="27fn1g3jg1siz" />

      <ClosingCTA data-id="element-1170" data-mp="1l6ppz6uxmr55" />
    </>;
}